// Finance/CashTransfer/CashTransferForm.tsx
import { useFormik } from 'formik';
import Select from 'react-select';
import { useTranslation } from 'react-i18next';
import { useAppDispatch } from '@store/hooks';
import { Form } from 'react-bootstrap';
import CustomAlert from '@components/Shared/Alert/CustomAlert';
import { addCashTransfer, updateCashTransfer } from '@store/Finance/CashTransfer/CashTransferSlice';
import Loading from '@components/Shared/Loader/Loading';
import useGetBankAccountsList from '@hooks/Finance/BankAccounts/useGetBankAccountsList';
import { useEffect, useState } from 'react';
import NeatixModal from '@components/Modal/NeatixModal';
import JournalsByReference from '../Journals/JournalsByReference/JournalsByReference';
import useGetOperatingUnitWithPermission from '@hooks/NTX/Organization/useGetOperatingUnitWithPermission';
// import { CashTransferSchema } from '../../../ValidationForm/validationSchema';

interface CashTransferFormProps {
  onclose: () => void;
  initialData?: any; // بيانات التحويل للتعديل
  isEditMode?: boolean; // وضع التعديل
}

const CashTransferForm = ({ onclose, initialData, isEditMode = false }: CashTransferFormProps) => {
  const { t, i18n } = useTranslation();
  const dispatch = useAppDispatch();
  const { OperatingUnitOptions, loading: loadingOperatingUnits } = useGetOperatingUnitWithPermission();

  const { OptionsBankAccounts, loading: accountsLoading, error: accountsError } = useGetBankAccountsList();
  const [showJournalModal, setShowJournalModal] = useState(false);

  // تهيئة الفورم
  const formik = useFormik({
    initialValues: {
      fromBankAccountId: initialData?.fromBankAccountId || '',
      toBankAccountId: initialData?.toBankAccountId || '',
      amount: initialData?.amount || 0,
      tranferFees: initialData?.tranferFees || 0,
      transferDate: initialData?.transferDate?.split('T')[0] || new Date().toISOString().split('T')[0],
      notes: initialData?.notes || '',
      transferVariance: initialData?.transferVariance || 0,
      confirmFlag: initialData?.confirmFlag || false,
      id: initialData?.id || '',
      //
      OperatingUnitFrom: '', // ليسهل ربطه مع الـ Select الخاص بالوحدات التشغيلية
      OperatingUnitTo: '' // ليسهل ربطه مع الـ Select الخاص بالوحدات التشغيلية
    },
    enableReinitialize: true,

    // validationSchema: CashTransferSchema(t),

    onSubmit: async (values, { resetForm, setSubmitting }) => {
      try {
        if (isEditMode && values.id) {
          // تحديث التحويل
          const result = await dispatch(
            updateCashTransfer({
              // id: values.id,
              // data: {
              ...values,
              id: values.id,
              amount: Number(values.amount),
              tranferFees: Number(values.tranferFees) || 0,
              transferVariance: Number(values.transferVariance) || 0,
              confirmFlag: Boolean(values.confirmFlag)
              // }
            })
          ).unwrap();

          if (result.succeeded) {
            resetForm();
            CustomAlert({
              action: 'Edit',
              msg: values.confirmFlag ? 'تم تحديث التحويل النقدي وتأكيده بنجاح' : 'تم تحديث التحويل النقدي كمسودة بنجاح'
            });
            onclose?.();
          } else {
            CustomAlert({ action: 'Error', msg: result.message });
          }
        } else {
          // إضافة تحويل جديد
          const result = await dispatch(
            addCashTransfer({
              ...values,
              amount: Number(values.amount),
              tranferFees: Number(values.tranferFees) || 0,
              transferVariance: Number(values.transferVariance) || 0,
              confirmFlag: Boolean(values.confirmFlag)
            })
          ).unwrap();

          if (result.succeeded) {
            resetForm();
            CustomAlert({
              action: 'Add',
              msg: values.confirmFlag ? 'تم إضافة التحويل النقدي وتأكيده بنجاح' : 'تم إضافة التحويل النقدي كمسودة بنجاح'
            });
            onclose?.();
          } else {
            CustomAlert({ action: 'Error', msg: result.message });
          }
        }
      } catch (error: any) {
        CustomAlert({ action: 'Error', msg: error.message });
      } finally {
        setSubmitting(false);
      }
    }
  });

  // حساب الإجمالي
  const calculateTotal = () => {
    const amount = Number(formik.values.amount) || 0;
    const fees = Number(formik.values.tranferFees) || 0;
    const variance = Number(formik.values.transferVariance) || 0;
    return amount + fees + variance;
  };

  // تعطيل الحقول في وضع التعديل إذا كانت مؤكدة
  const isConfirmed = isEditMode && initialData?.confirmFlag;
  const isPosted = isEditMode && initialData?.postedFlag;

  return (
    <>
      <Loading loading={accountsLoading} error={accountsError}>
        {isEditMode && isPosted && (
          <div className='alert alert-info d-flex align-items-center justify-content-between mb-2 rounded-2'>
            <div className='d-flex align-items-center'>
              <i className='fas fa-check-circle fa-2x me-3 text-info'></i>
              <div>
                <h6 className='mb-0 fw-bold'>{t('تم ترحيل التحويل')}</h6>
                <small>{t('لا يمكن تعديل التحويل بعد الترحيـل ')}</small>
              </div>
            </div>
            <button type='button' className='btn btn-outline-info' onClick={() => setShowJournalModal(true)}>
              <i className='fas fa-file-invoice me-2'></i>
              {t('عرض القيد')}
            </button>
          </div>
        )}
        <Form onSubmit={formik.handleSubmit} noValidate>
          {/* معلومات الحسابات */}
          <div className='row  '>
            <div className='col-lg-6'>
              <div className='form-group mb-3'>
                <label className='form-label small fw-bold text-muted'>{t('من وحدة تشغيل')}</label>
                <Select
                  classNamePrefix='react-select'
                  isLoading={loadingOperatingUnits}
                  isSearchable={true}
                  isClearable
                  options={OperatingUnitOptions}
                  value={OperatingUnitOptions?.find((f: any) => f.value === formik.values.OperatingUnitFrom)}
                  onChange={(option: any) => formik.setFieldValue('OperatingUnitFrom', option?.value || '')}
                  placeholder={t('اختر...')}
                  menuPortalTarget={document.body}
                  styles={{
                    menuPortal: (base: any) => ({ ...base, zIndex: 9999 })
                  }}
                />
              </div>
            </div>

            <div className='col-lg-6'>
              <div className='form-group mb-3'>
                <label className='form-label fw-bold'>
                  من الحساب <span className='text-danger'>*</span>
                </label>
                <Select
                  className={`react-select-container ${
                    formik.touched.fromBankAccountId && formik.errors.fromBankAccountId ? 'is-invalid' : ''
                  }`}
                  classNamePrefix='react-select'
                  isLoading={accountsLoading}
                  isSearchable={true}
                  isClearable
                  options={OptionsBankAccounts.filter(f => f.organizationId === formik.values.OperatingUnitFrom)}
                  value={OptionsBankAccounts?.find((f: any) => f.value === formik.values.fromBankAccountId)}
                  onChange={(option: any) => {
                    formik.setFieldValue('fromBankAccountId', option?.value || '');
                    formik.setFieldValue('toBankAccountId', ''); // إعادة تعيين الحساب المستقبل
                  }}
                  onBlur={() => formik.setFieldTouched('fromBankAccountId', true)}
                  placeholder='اختر الحساب المصدر'
                  isDisabled={isConfirmed || isPosted || formik.isSubmitting}
                />
                {formik.touched.fromBankAccountId && formik.errors.fromBankAccountId && (
                  <div className='invalid-feedback d-block'>{String(formik.errors.fromBankAccountId)}</div>
                )}
              </div>
            </div>
            <div className='col-lg-6'>
              <div className='form-group mb-3'>
                <label className='form-label small fw-bold text-muted'>{t('إلى وحدة تشغيل')}</label>
                <Select
                  classNamePrefix='react-select'
                  isLoading={loadingOperatingUnits}
                  isSearchable={true}
                  isClearable
                  options={OperatingUnitOptions}
                  value={OperatingUnitOptions?.find((f: any) => f.value === formik.values.OperatingUnitTo)}
                  onChange={(option: any) => formik.setFieldValue('OperatingUnitTo', option?.value || '')}
                  placeholder={t('اختر...')}
                  menuPortalTarget={document.body}
                  styles={{
                    menuPortal: (base: any) => ({ ...base, zIndex: 9999 })
                  }}
                />
              </div>
            </div>

            <div className='col-lg-6'>
              <div className='form-group mb-3'>
                <label className='form-label fw-bold'>
                  إلى الحساب <span className='text-danger'>*</span>
                </label>
                <Select
                  className={`react-select-container ${
                    formik.touched.toBankAccountId && formik.errors.toBankAccountId ? 'is-invalid' : ''
                  }`}
                  classNamePrefix='react-select'
                  isLoading={accountsLoading}
                  isSearchable={true}
                  isClearable
                  options={OptionsBankAccounts?.filter(
                    (acc: any) =>
                      acc.value !== formik.values.fromBankAccountId &&
                      acc.organizationId === formik.values.OperatingUnitTo
                  )}
                  value={OptionsBankAccounts?.find((f: any) => f.value === formik.values.toBankAccountId)}
                  onChange={(option: any) => formik.setFieldValue('toBankAccountId', option?.value || '')}
                  onBlur={() => formik.setFieldTouched('toBankAccountId', true)}
                  placeholder='اختر الحساب المستلم'
                  isDisabled={!formik.values.fromBankAccountId || isConfirmed || isPosted || formik.isSubmitting}
                />
                {formik.touched.toBankAccountId && formik.errors.toBankAccountId && (
                  <div className='invalid-feedback d-block'>{String(formik.errors.toBankAccountId)}</div>
                )}
              </div>
            </div>

            <div className='col-lg-6'>
              <div className='form-group mb-3'>
                <label className='form-label fw-bold'>
                  تاريخ التحويل <span className='text-danger'>*</span>
                </label>
                <Form.Control
                  type='date'
                  name='transferDate'
                  onChange={formik.handleChange}
                  onBlur={formik.handleBlur}
                  value={formik.values.transferDate}
                  isInvalid={!!(formik.touched.transferDate && formik.errors.transferDate)}
                  disabled={isConfirmed || isPosted || formik.isSubmitting}
                />
                <Form.Control.Feedback type='invalid'>{String(formik.errors.transferDate)}</Form.Control.Feedback>
              </div>
            </div>

            {/* المبلغ والرسوم والفروقات */}

            <div className='col-lg-6'>
              <div className='form-group mb-3'>
                <label className='form-label fw-bold'>
                  المبلغ <span className='text-danger'>*</span>
                </label>
                <div className='input-group'>
                  <Form.Control
                    type='number'
                    step='0.01'
                    min='0'
                    name='amount'
                    onChange={formik.handleChange}
                    onBlur={formik.handleBlur}
                    value={formik.values.amount}
                    isInvalid={!!(formik.touched.amount && formik.errors.amount)}
                    placeholder='أدخل المبلغ'
                    disabled={isConfirmed || isPosted || formik.isSubmitting}
                  />
                </div>
                <Form.Control.Feedback type='invalid'>{String(formik.errors.amount)}</Form.Control.Feedback>
              </div>
            </div>

            <div className='col-lg-4'>
              <div className='form-group mb-3'>
                <label className='form-label fw-bold'>رسوم التحويل</label>
                <div className='input-group'>
                  <Form.Control
                    type='number'
                    step='0.01'
                    min='0'
                    name='tranferFees'
                    onChange={formik.handleChange}
                    onBlur={formik.handleBlur}
                    value={formik.values.tranferFees}
                    placeholder='أدخل الرسوم'
                    disabled={isConfirmed || isPosted || formik.isSubmitting}
                  />
                </div>
              </div>
            </div>

            <div className='col-lg-4'>
              <div className='form-group mb-3'>
                <label className='form-label fw-bold'>فروقات التحويل</label>
                <div className='input-group'>
                  <Form.Control
                    type='number'
                    step='0.01'
                    name='transferVariance'
                    onChange={formik.handleChange}
                    onBlur={formik.handleBlur}
                    value={formik.values.transferVariance}
                    placeholder='أدخل الفروقات'
                    disabled={isConfirmed || isPosted || formik.isSubmitting}
                  />
                </div>
              </div>
            </div>

            <div className='col-lg-4'>
              <div className='form-group mb-3'>
                <label className='form-label fw-bold'>الإجمالي</label>
                <div className='input-group'>
                  <Form.Control
                    type='text'
                    value={calculateTotal().toLocaleString()}
                    readOnly
                    className={`fw-bold text-primary  `}
                  />
                </div>
              </div>
            </div>
          </div>

          {/* التاريخ والملاحظات */}
          <div className='row mb-4'>
            {/* <div className='col-lg-9'>
              <div className='form-group mb-3'>
                <label className='form-label fw-bold'>ملاحظات</label>
                <Form.Control
                  as='textarea'
                  rows={2}
                  name='notes'
                  onChange={formik.handleChange}
                  onBlur={formik.handleBlur}
                  value={formik.values.notes}
                  placeholder='أدخل ملاحظاتك'
                  disabled={isConfirmed || isPosted || formik.isSubmitting}
                />
              </div>
            </div> */}
          </div>

          {/* حالة التحويل (للقراءة فقط في وضع التعديل) */}
          {/* {isEditMode && (
            <div className='row mb-4'>
              <div className='col-lg-6'>
                <div className='alert alert-info py-2'>
                  <div className='d-flex justify-content-between align-items-center'>
                    <div>
                      <i className='fas fa-info-circle me-2'></i>
                      <strong>حالة التحويل:</strong>
                    </div>
                    <div>
                      {initialData?.confirmFlag ? (
                        <span className='badge bg-success'>مؤكد</span>
                      ) : (
                        <span className='badge bg-warning text-dark'>مسودة</span>
                      )}
                      {initialData?.postedFlag && <span className='badge bg-primary ms-2'>مرحل</span>}
                    </div>
                  </div>
                </div>
              </div>
            </div>
          )} */}

          {/* الأزرار */}
          <div className='d-flex justify-content-end gap-3 pt-3 border-top'>
            {/* زر الحفظ المزدوج */}
            {!isConfirmed || !isPosted ? (
              <div className='btn-group'>
                {/* الزر الظاهر: مسودة */}
                <button
                  type='button'
                  onClick={async () => {
                    await formik.setFieldValue('confirmFlag', false);
                    await formik.submitForm();
                  }}
                  disabled={formik.isSubmitting || !formik.isValid}
                  className='btn btn-theme px-4'
                >
                  {formik.isSubmitting && formik.values.confirmFlag === false ? (
                    <>
                      <span className='spinner-border spinner-border-sm me-2'></span>
                      جاري الحفظ...
                    </>
                  ) : (
                    <>
                      <i className='fas fa-file-edit me-2'></i>
                      {isEditMode ? 'تحديث كمسودة' : 'حفظ كمسودة'}
                    </>
                  )}
                </button>

                {/* سهم الدروبداون */}
                <button
                  type='button'
                  className='btn btn-theme dropdown-toggle dropdown-toggle-split'
                  data-bs-toggle='dropdown'
                  aria-expanded='false'
                  disabled={formik.isSubmitting || !formik.isValid}
                >
                  <span className='visually-hidden'>Toggle Dropdown</span>
                </button>

                {/* القائمة */}
                <ul className='dropdown-menu dropdown-menu-end'>
                  <li>
                    <button
                      type='button'
                      className='dropdown-item fw-bold'
                      disabled={formik.isSubmitting || !formik.isValid}
                      onClick={async () => {
                        await formik.setFieldValue('confirmFlag', true);
                        await formik.submitForm();
                      }}
                    >
                      <i className='fas fa-check-circle me-2 text-theme'></i>
                      {isEditMode ? 'تحديث وتأكيد نهائي' : 'حفظ وتأكيد نهائي'}
                    </button>
                  </li>
                </ul>
              </div>
            ) : (
              // إذا كان مؤكد أو مرحل، نعرض رسالة فقط
              <div className='alert alert-warning py-2 mb-0'>
                <i className='fas fa-lock me-2'></i>
                التحويل {isPosted ? 'مرحل' : 'مؤكد'} - لا يمكن التعديل
              </div>
            )}

            <button type='button' className='btn btn-default' onClick={onclose} disabled={formik.isSubmitting}>
              <i className='fas fa-times me-2'></i>
              إلغاء
            </button>
          </div>
        </Form>
      </Loading>

      {/* Modal لعرض القيد */}
      <NeatixModal show={showJournalModal} setShow={setShowJournalModal} size='xl' title={t('تفاصيل القيد')} centered>
        {initialData?.id && <JournalsByReference referenceId={initialData?.id} />}
      </NeatixModal>
    </>
  );
};

export default CashTransferForm;
